import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { ArrowDownIcon, Input, SimpleInput } from 'cat-ecommerce-alloy';
import Field from '@app/components/common/form/Field';
import {
  Drawer,
  DrawerBody,
  DrawerHeader,
  useDrawer
} from '@cat-ecom/pcc-components';
import { useTranslation } from 'react-i18next';
import { CatList } from '@blocks-react/components/List';
import { CatListItem } from '@blocks-react/components/ListItem';
import CustomHeaderDrawer from '@app/components/common/CustomDrawer/CustomHeaderDrawer/CustomHeaderDrawer';
import { CatButton } from '@blocks-react/components/Button';
import { CatIconArrowBack } from '@blocks-react/components/Icons/ArrowBack';
import { CatSearchForm } from '@blocks-react/components/SearchForm';
import { isEmpty } from '@app/utils';
import styles from './DrawerDropdown.module.scss';
import cx from 'classnames';

export const DRAWER_DROPDOWN_HEADER_VARIANTS = {
  SLIM: 'slim',
  REGULAR: 'regular'
};

export const DRAWER_DROPDOWN_ORIENTATION = {
  BOTTOM: 'bottom',
  SIDE: 'side'
};

export const DRAWER_DROPDOWN_VERTICAL_MAX_HEIGHT = {
  DEFAULT: 'default',
  LARGE: 'large'
};

const DEFAULT_ITEM_TO_STRING = (val = '') => val ?? '';

const DrawerDropdown = ({
  capitalizeLabel = true,
  selected,
  onChange,
  label,
  name,
  disabled,
  itemToString = DEFAULT_ITEM_TO_STRING,
  items,
  closeOnSelect = true,
  dataTestId = 'drawer-dropdown',
  headerVariant = DRAWER_DROPDOWN_HEADER_VARIANTS.SLIM,
  orientation = DRAWER_DROPDOWN_ORIENTATION.BOTTOM,
  verticalMaxHeight = DRAWER_DROPDOWN_VERTICAL_MAX_HEIGHT.DEFAULT,
  placeholder,
  showRequired = false,
  validator,
  isCustomRender = false,
  customRender,
  isPaymentAndSummaryPage = false,
  showBackButton = false,
  withSearch = false,
  searchPlaceHolder,
  hideFieldLabel = false,
  containerClassName = 'w-100'
}) => {
  const { t } = useTranslation();
  const textTransformStyles = {
    'text-capitalize': capitalizeLabel,
    'text-uppercase': !capitalizeLabel
  };
  const [filteredItems, setFilteredItems] = useState(items);
  const selectedLabel = itemToString(selected);
  const {
    drawerProps: defaultDrawerProps,
    openDrawer,
    isDrawerOpen,
    closeDrawer: onCloseDrawer
  } = useDrawer({
    hasBackdrop: true
  });

  const closeDrawer = () => {
    if (filteredItems.length !== items.length) {
      setFilteredItems(items);
    }
    onCloseDrawer();
  };

  const handleSelect = item => {
    onChange(item);
    if (closeOnSelect) {
      closeDrawer();
    }
  };

  const drawerProps = {
    ...defaultDrawerProps,
    onBlDrawerClose: e => {
      e.stopPropagation();
      closeDrawer();
    }
  };

  const isSelectedLabelString = useMemo(
    () => typeof selectedLabel === 'string',
    [selectedLabel]
  );

  const inputValue = isSelectedLabelString ? selectedLabel : selectedLabel?.id;

  const onSearchInputChange = e => {
    const searchText = e.target.value;
    const currentItems = items.filter(item =>
      itemToString(item).toLowerCase().includes(searchText.toLowerCase())
    );
    setFilteredItems(currentItems);
  };

  const fieldNote = isEmpty(filteredItems)
    ? t('NO_RESULTS_TRY_ANOTHER_SEARCH')
    : '';

  return (
    <div className={containerClassName}>
      {!hideFieldLabel && (
        <label
          htmlFor={name}
          className={cx(
            styles['drawer-dropdown__label'],
            'font-regular',
            'text-univers--bold',
            textTransformStyles
          )}
        >
          {label}
          {showRequired && '*'}
        </label>
      )}
      <button
        className={cx(
          'w-100 p-0 mb-3 border-0 position-relative',
          styles['drawer-dropdown__open-btn']
        )}
        name={name}
        data-testid={dataTestId}
        disabled={disabled}
        onClick={e => {
          e.preventDefault();
          openDrawer();
        }}
      >
        <div className={cx(styles.cover, 'w-100 h-100 position-absolute')} />
        <div
          className={cx(
            styles['input-container'],
            'flex items-center justify-center'
          )}
        >
          {isPaymentAndSummaryPage ? (
            <SimpleInput
              value={inputValue}
              name={name}
              id={name}
              disabled
              placeholder={placeholder}
            />
          ) : (
            <Field
              type="text"
              as={Input}
              value={inputValue}
              name={name}
              id={name}
              placeholder={placeholder}
              showRequired={showRequired}
              validator={validator}
            />
          )}
          <ArrowDownIcon className={cx(styles.icon, 'position-absolute')} />
        </div>
      </button>
      <Drawer
        {...drawerProps}
        isActive={isDrawerOpen}
        orientation={orientation}
        verticalMaxHeight={verticalMaxHeight}
        onBlDrawerClose={closeDrawer}
        hideCloseButton={
          headerVariant !== DRAWER_DROPDOWN_HEADER_VARIANTS.REGULAR
        }
      >
        {headerVariant === DRAWER_DROPDOWN_HEADER_VARIANTS.REGULAR ? (
          <DrawerHeader className={'d-flex align-items-center gap-3'}>
            {showBackButton && (
              <CatButton
                className={cx('p-2', styles['drawer-dropdown__arrow-btn'])}
                data-testid="back-button"
                hideText
                variant="ghost"
                size="md"
                onClick={closeDrawer}
              >
                <CatIconArrowBack slot="before" />
              </CatButton>
            )}
            <h4
              className={cx(
                textTransformStyles,
                'cat-u-theme-typography-title m-0',
                styles['drawer-dropdown__title']
              )}
            >
              {label}
            </h4>
          </DrawerHeader>
        ) : (
          <CustomHeaderDrawer title={label} onCloseDrawer={closeDrawer} />
        )}
        <DrawerBody
          className={cx({
            'pt-3 pb-5': !withSearch,
            'd-flex flex-column gap-3': withSearch
          })}
        >
          {withSearch && (
            <CatSearchForm
              onBlChange={onSearchInputChange}
              autocomplete="off"
              size="md"
              mobileBreakPoint={1}
              placeholder={searchPlaceHolder}
              fieldNote={fieldNote}
              isEmpty
            />
          )}
          <CatList>
            {filteredItems.map(item => {
              const itemLabel = itemToString(item);
              const isItemLabelString = typeof itemLabel === 'string';

              const element = {
                id: isItemLabelString ? itemLabel : itemLabel?.id,
                component: isItemLabelString ? itemLabel : itemLabel?.component,
                selectedElement: isSelectedLabelString
                  ? selectedLabel
                  : selectedLabel?.id
              };

              return (
                <CatListItem
                  key={element.id}
                  isActive={element.selectedElement === element.id}
                  onBlSelect={() => handleSelect(item)}
                  className={styles['drawer-dropdown__item-custom-padding']}
                >
                  <div className={styles['drawer-dropdown__item-padding']}>
                    {element.component}
                    {isCustomRender && <div>{customRender(item)}</div>}
                  </div>
                </CatListItem>
              );
            })}
          </CatList>
        </DrawerBody>
      </Drawer>
    </div>
  );
};

DrawerDropdown.propTypes = {
  items: PropTypes.arrayOf(PropTypes.shape({})),
  onChange: PropTypes.func,
  isPaymentAndSummaryPage: PropTypes.bool,
  itemToString: PropTypes.func,
  capitalizeLabel: PropTypes.bool,
  selected: PropTypes.shape({}),
  name: PropTypes.string,
  label: PropTypes.string,
  dataTestId: PropTypes.string,
  disabled: PropTypes.bool,
  closeOnSelect: PropTypes.bool,
  headerVariant: PropTypes.oneOf(
    Object.values(DRAWER_DROPDOWN_HEADER_VARIANTS)
  ),
  orientation: PropTypes.oneOf(Object.values(DRAWER_DROPDOWN_ORIENTATION)),
  verticalMaxHeight: PropTypes.oneOf(
    Object.values(DRAWER_DROPDOWN_VERTICAL_MAX_HEIGHT)
  ),
  placeholder: PropTypes.string,
  showRequired: PropTypes.bool,
  isCustomRender: PropTypes.bool,
  customRender: PropTypes.func,
  validator: PropTypes.func,
  showBackButton: PropTypes.bool,
  withSearch: PropTypes.bool,
  searchPlaceHolder: PropTypes.string,
  hideFieldLabel: PropTypes.bool,
  containerClassName: PropTypes.string
};

export default DrawerDropdown;
